import React, { useEffect, useRef, useState } from 'react';
import { Button, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { FormElement, FormSection } from '../../api/formApi';
import { EvidenceRef } from '../../types/healthcare/PatientAnalysisTypes';
import FormFieldRenderer from './FormFieldRenderer';
import { isFullWidthFieldType } from './fieldTypes';

const { Text } = Typography;

type FieldValues = Record<string | number, any>;
type FieldFiles = Record<string | number, File[]>;

export interface FormRendererProps {
  /** Either a flat field list or an array of sections (when useSections). */
  structure: (FormElement | FormSection)[];
  useSections?: boolean;
  values: FieldValues;
  onValuesChange: (values: FieldValues) => void;
  files?: FieldFiles;
  onFilesChange?: (files: FieldFiles) => void;
  onValidityChange?: (valid: boolean) => void;
  readOnly?: boolean;
  /**
   * Fields that stay read-only while the rest of the form is editable, by field id.
   *
   * Optional and inert when omitted (the same additive shape as `submitTestId`), so this
   * changes nothing for every existing consumer — `readOnly` alone still governs the whole
   * form.
   *
   * It exists for F9's edit mode, where a practitioner corrects a submitted patient
   * response but MUST NOT touch the patient's signature. That cannot be expressed by the
   * caller, because `readOnly` is all-or-nothing: flipping the whole form editable puts the
   * signature on a canvas with a "Clear" button that nulls it. Listing the field here keeps
   * it on the read-only `<img>` branch: visible, and with no control that can alter it.
   *
   * (The editable canvas does now paint an existing `value` — 86d3y4ea7 — so this is no
   * longer also covering for a signature that would otherwise vanish from the screen. The
   * reason it is still required is the one above: showing a signature and licensing its
   * replacement are different things, and only the caller knows which it wants.)
   */
  readOnlyFieldIds?: ReadonlySet<string | number>;
  /** 'wizard' shows one section at a time with nav; 'flat' shows everything stacked. */
  sectionMode?: 'wizard' | 'flat';
  title?: string;
  /** Called when the user finishes the final section (wizard mode). */
  onSubmit?: () => void;
  submitting?: boolean;
  submitLabel?: string;
  /**
   * `data-testid` for the wizard's submit button. Optional and inert when omitted (React
   * drops an `undefined` attribute), so this changes nothing for existing consumers — it
   * exists because the submit control is owned here, and a wrapper cannot anchor it.
   */
  submitTestId?: string;
  /** Wizard starting section (e.g. restored from saved progress). */
  initialSection?: number;
  /** Fired when the wizard moves between sections. */
  onSectionChange?: (index: number) => void;
  /** Per-field evidence references (e.g. for AI-filled fields) keyed by field id. */
  fieldRefs?: Record<string, EvidenceRef[]>;
}

const PSEUDO_SECTION_ID = '__form-renderer-single';

/** Normalise the structure into sections so rendering is uniform. */
const toSections = (
  structure: (FormElement | FormSection)[],
  useSections: boolean,
): FormSection[] => {
  if (useSections) {
    return (Array.isArray(structure) ? (structure as FormSection[]) : []).filter(Boolean);
  }
  return [{
    id: PSEUDO_SECTION_ID,
    title: '',
    fields: (Array.isArray(structure) ? (structure as FormElement[]) : []),
    order: 0,
  }];
};

/** Flatten all fields across sections (or a flat structure). */
export const flattenFields = (
  structure: (FormElement | FormSection)[],
  useSections: boolean,
): FormElement[] => toSections(structure, useSections).flatMap((s) => s.fields ?? []);

function useContainerCols(ref: React.RefObject<HTMLDivElement | null>, breakpoint = 420): number {
  const [cols, setCols] = useState(2);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setCols(entry.contentRect.width >= breakpoint ? 2 : 1);
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, breakpoint]);
  return cols;
}

/**
 * Unified, compact form renderer. Owns section navigation, required-field
 * validation and value state plumbing; field rendering is delegated to
 * FormFieldRenderer so every surface renders forms identically.
 */
const FormRenderer: React.FC<FormRendererProps> = ({
  structure,
  useSections = false,
  values,
  onValuesChange,
  files,
  onFilesChange,
  onValidityChange,
  readOnly = false,
  readOnlyFieldIds,
  sectionMode = 'wizard',
  title,
  onSubmit,
  submitting,
  submitLabel = 'Submit',
  submitTestId,
  initialSection = 0,
  onSectionChange,
  fieldRefs,
}) => {
  const sections = toSections(structure, useSections);
  const gridRef = useRef<HTMLDivElement>(null);
  const cols = useContainerCols(gridRef);

  const [currentSection, setCurrentSection] = useState(initialSection);
  const [showErrors, setShowErrors] = useState(false);

  const changeSection = (index: number) => {
    setCurrentSection(index);
    onSectionChange?.(index);
  };

  const isWizard = !readOnly && sectionMode === 'wizard';
  const errorsEnabled = !readOnly && sectionMode !== 'flat';
  const safeIndex = Math.min(currentSection, Math.max(sections.length - 1, 0));

  const isFilled = (f: FormElement): boolean => {
    if (f.type === 'file') return !!(files?.[f.id]?.length);
    const v = values[f.id];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== '' && v !== null;
  };

  const isRequired = (f: FormElement) =>
    !!f.required && f.type !== 'heading' && f.type !== 'paragraph';

  const allFields = sections.flatMap((s) => s.fields ?? []);
  const requiredFields = allFields.filter(isRequired);

  // Emit overall validity to the wrapper.
  useEffect(() => {
    onValidityChange?.(requiredFields.every(isFilled));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, files, structure]);

  const setValue = (id: string | number, value: any) => {
    onValuesChange({ ...values, [id]: value });
  };
  const setFiles = (id: string | number, list: File[]) => {
    onFilesChange?.({ ...(files || {}), [id]: list });
  };

  const errorFor = (f: FormElement): string | undefined =>
    errorsEnabled && showErrors && isRequired(f) && !isFilled(f)
      ? 'This field is required'
      : undefined;

  const sectionIsValid = (fields: FormElement[]) =>
    fields.filter(isRequired).every(isFilled);

  const goNext = () => {
    if (!sectionIsValid(sections[safeIndex]?.fields ?? [])) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    changeSection(safeIndex + 1);
  };

  const goPrev = () => {
    setShowErrors(false);
    changeSection(Math.max(safeIndex - 1, 0));
  };

  const handleSubmit = () => {
    const firstInvalid = sections.findIndex((s) => !sectionIsValid(s.fields ?? []));
    if (firstInvalid !== -1) {
      setCurrentSection(firstInvalid);
      setShowErrors(true);
      return;
    }
    onSubmit?.();
  };

  const renderGrid = (fields: FormElement[]) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: '6px 12px',
      }}
    >
      {fields.map((field) => (
        <div key={field.id} style={isFullWidthFieldType(field.type) ? { gridColumn: '1 / -1' } : undefined}>
          <FormFieldRenderer
            field={field}
            value={values[field.id]}
            onChange={(v) => setValue(field.id, v)}
            files={files?.[field.id]}
            onFilesChange={(list) => setFiles(field.id, list)}
            readOnly={readOnly || !!readOnlyFieldIds?.has(field.id)}
            error={errorFor(field)}
            refs={fieldRefs?.[field.id]}
          />
        </div>
      ))}
    </div>
  );

  const renderSectionHeader = (section: FormSection) => {
    if (!useSections || (!section.title && !section.description)) return null;
    return (
      <div style={{ marginBottom: 6 }}>
        {section.title && (
          <Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--mg-text)', display: 'block' }}>
            {section.title}
          </Text>
        )}
        {section.description && (
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)', display: 'block' }}>
            {section.description}
          </Text>
        )}
      </div>
    );
  };

  const multiSection = useSections && sections.length > 1;

  return (
    <div ref={gridRef} style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      {title && (
        <Text style={{ fontSize: 12, fontWeight: 600, color: 'var(--mg-text)' }}>{title}</Text>
      )}

      {isWizard ? (
        <>
          {renderSectionHeader(sections[safeIndex])}
          {renderGrid(sections[safeIndex]?.fields ?? [])}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            {multiSection ? (
              <>
                <Button size="small" icon={<LeftOutlined />} onClick={goPrev} disabled={safeIndex === 0}>
                  Back
                </Button>
                <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
                  Section {safeIndex + 1} of {sections.length}
                </Text>
                {safeIndex < sections.length - 1 ? (
                  <Button size="small" type="primary" onClick={goNext}>
                    Next <RightOutlined />
                  </Button>
                ) : (
                  <Button size="small" type="primary" loading={submitting} onClick={handleSubmit} data-testid={submitTestId}>
                    {submitLabel}
                  </Button>
                )}
              </>
            ) : (
              <>
                <span />
                <Button size="small" type="primary" loading={submitting} onClick={handleSubmit} data-testid={submitTestId}>
                  {submitLabel}
                </Button>
              </>
            )}
          </div>
        </>
      ) : (
        // Flat / read-only: render every section stacked.
        sections.map((section) => (
          <div key={section.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {renderSectionHeader(section)}
            {renderGrid(section.fields ?? [])}
          </div>
        ))
      )}
    </div>
  );
};

export default FormRenderer;
